// src/pages/AdminMessages.js - Gestion des messages de contact (Admin)
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './AdminMessages.css';

const API_URL = process.env.REACT_APP_API_URL;

function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread, read

  const chargerMessages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        setError('Vous devez être connecté comme admin');
        return;
      }

      const response = await fetch(`${API_URL}/contact/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessages(data.messages || []);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    chargerMessages();
  }, [chargerMessages]);

  const marquerCommeLu = async (id) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      await fetch(`${API_URL}/contact/messages/${id}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessages(prev => prev.map(m => 
        m.id === id ? { ...m, lu: true } : m
      ));
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const supprimerMessage = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      await fetch(`${API_URL}/contact/messages/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessages(prev => prev.filter(m => m.id !== id));
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const ouvrirMessage = (message) => {
    setSelectedMessage(message);
    if (!message.lu) {
      marquerCommeLu(message.id);
    }
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'unread') return !m.lu;
    if (filter === 'read') return m.lu;
    return true;
  });

  const messagesNonLus = messages.filter(m => !m.lu).length;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-messages">
        <div className="loading-state">
          <span className="spinner">⏳</span>
          <p>Chargement des messages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-messages">
        <div className="error-state">
          <span>❌</span>
          <p>{error}</p>
          <button onClick={chargerMessages}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-messages">
      {/* Header */}
      <header className="messages-header">
        <div className="header-left">
          <h1>📧 Messages de contact</h1>
          {messagesNonLus > 0 && (
            <span className="badge-unread">{messagesNonLus} non lu{messagesNonLus > 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="header-actions">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Tous ({messages.length})</option>
            <option value="unread">Non lus ({messagesNonLus})</option>
            <option value="read">Lus ({messages.length - messagesNonLus})</option>
          </select>
          <button onClick={chargerMessages} className="btn-refresh">🔄 Actualiser</button>
          <Link to="/admin" className="btn-back">← Dashboard</Link>
        </div>
      </header>

      <div className="messages-container">
        {/* Liste des messages */}
        <div className="messages-list">
          {filteredMessages.length === 0 ? (
            <div className="empty-state">
              <span>📭</span>
              <p>Aucun message {filter === 'unread' ? 'non lu' : filter === 'read' ? 'lu' : ''}</p>
            </div>
          ) : (
            filteredMessages.map(message => (
              <div 
                key={message.id} 
                className={`message-item ${!message.lu ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                onClick={() => ouvrirMessage(message)}
              >
                <div className="message-header">
                  <span className="message-nom">{message.nom}</span>
                  {!message.lu && <span className="dot-unread"></span>}
                </div>
                <div className="message-sujet">{message.sujet}</div>
                <div className="message-preview">{message.message.substring(0, 80)}...</div>
                <div className="message-date">{formatDate(message.created_at)}</div>
              </div>
            ))
          )}
        </div>

        {/* Détail du message */}
        <div className="message-detail">
          {selectedMessage ? (
            <>
              <div className="detail-header">
                <h2>{selectedMessage.sujet}</h2>
                <div className="detail-actions">
                  <a 
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.sujet}`} 
                    className="btn-reply"
                  >
                    ↩️ Répondre
                  </a>
                  <a 
                    href={`https://wa.me/${selectedMessage.email.includes('@') ? '' : selectedMessage.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp"
                    style={{ display: 'none' }}
                  >
                    📱 WhatsApp
                  </a>
                  <button 
                    onClick={() => supprimerMessage(selectedMessage.id)} 
                    className="btn-delete"
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              </div>

              <div className="detail-info">
                <div className="info-item">
                  <strong>De:</strong> {selectedMessage.nom}
                </div>
                <div className="info-item">
                  <strong>Email:</strong> 
                  <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                </div>
                <div className="info-item">
                  <strong>Date:</strong> {formatDate(selectedMessage.created_at)}
                </div>
              </div>

              <div className="detail-message">
                <p>{selectedMessage.message}</p>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <span>📩</span>
              <p>Sélectionnez un message pour le lire</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminMessages;
